(() => {
  if (customElements.get('hb-complete-planting')) return;

  const FREE_DELIVERY_FALLBACK = 4000;

  class HBCompletePlanting extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.loading = false;
      this.loaded = false;
      this.boundClick = this.handleClick.bind(this);
      this.boundCartChange = this.handleCartChange.bind(this);
      this.addEventListener('click', this.boundClick);
      document.addEventListener('eurus:cart:items-changed', this.boundCartChange);
      document.addEventListener('hb:cart-upsell-added', this.boundCartChange);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.loadRecommendations();
        }, { rootMargin: '260px 0px' });
        this.observer.observe(this);
      } else {
        this.loadRecommendations();
      }

      this.fallbackTimer = window.setTimeout(() => this.loadRecommendations(), 1400);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.fallbackTimer) window.clearTimeout(this.fallbackTimer);
      if (this.cartRefreshTimer) window.clearTimeout(this.cartRefreshTimer);
      this.removeEventListener('click', this.boundClick);
      document.removeEventListener('eurus:cart:items-changed', this.boundCartChange);
      document.removeEventListener('hb:cart-upsell-added', this.boundCartChange);
    }

    async loadRecommendations() {
      if (this.loading || this.loaded) return;
      this.loading = true;

      try {
        let result = await this.fetchRecommendations(this.dataset.complementaryUrl);
        if (!result || !result.querySelector('[data-hb-upsell-card]')) {
          result = await this.fetchRecommendations(this.dataset.relatedUrl);
        }

        if (!result || !result.querySelector('[data-hb-upsell-card]')) {
          this.hidden = true;
          return;
        }

        const sourceContent = result.querySelector('[data-hb-upsell-content]');
        const targetContent = this.querySelector('[data-hb-upsell-content]');
        if (!sourceContent || !targetContent) {
          this.hidden = true;
          return;
        }

        targetContent.innerHTML = sourceContent.innerHTML;
        this.loaded = true;
        this.hidden = false;
        await this.refreshCartStatus();
        this.track('upsell_view', { count: this.querySelectorAll('[data-hb-upsell-card]').length });
      } catch (error) {
        this.hidden = true;
      } finally {
        this.loading = false;
        if (this.fallbackTimer) window.clearTimeout(this.fallbackTimer);
      }
    }

    async fetchRecommendations(url) {
      if (!url) return null;
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' }
      });
      if (!response.ok) return null;
      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      return documentFragment.getElementById(this.id);
    }

    handleClick(event) {
      const viewLink = event.target.closest('[data-hb-upsell-view]');
      if (!viewLink || !this.contains(viewLink)) return;
      const card = viewLink.closest('[data-hb-upsell-card]');
      this.track('upsell_product_opened', {
        product_handle: card ? card.dataset.productHandle : ''
      });
    }

    handleCartChange() {
      if (this.cartRefreshTimer) window.clearTimeout(this.cartRefreshTimer);
      this.cartRefreshTimer = window.setTimeout(() => this.refreshCartStatus(), 120);
    }

    async refreshCartStatus() {
      try {
        const response = await fetch('/cart.js', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) return;
        const cart = await response.json();
        this.updateDeliveryMessage(cart);
      } catch (error) {
        const delivery = this.querySelector('[data-hb-upsell-delivery]');
        if (delivery) delivery.textContent = 'Free delivery over £40';
      }
    }

    updateDeliveryMessage(cart) {
      const delivery = this.querySelector('[data-hb-upsell-delivery]');
      if (!delivery) return;
      const threshold = Number(this.dataset.freeDeliveryThreshold) || FREE_DELIVERY_FALLBACK;
      const total = Number(cart && cart.total_price) || 0;
      if (total >= threshold) {
        delivery.textContent = 'Free delivery unlocked';
        return;
      }
      delivery.textContent = `${this.formatMoney(threshold - total, cart.currency)} away from free delivery`;
    }

    formatMoney(pence, currency) {
      try {
        return new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency: currency || 'GBP',
          minimumFractionDigits: pence % 100 === 0 ? 0 : 2
        }).format(pence / 100);
      } catch (error) {
        return `£${(pence / 100).toFixed(2)}`;
      }
    }

    track(eventName, detail = {}) {
      const payload = Object.assign({
        event: eventName,
        source_product_id: this.dataset.productId
      }, detail);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
      if (typeof window.clarity === 'function') window.clarity('event', eventName);
    }
  }

  customElements.define('hb-complete-planting', HBCompletePlanting);
})();
